import { track, EVENTS } from '../analytics';

// Diagnostics for failed transcript saves (Cath: "1hr+ live transcription could
// not be saved when stopped", Retry then works). The save paths used to swallow
// the error, so the cause was invisible. This records WHY a save failed, never
// WHAT was being saved: only error metadata, text lengths and the auth token's
// remaining lifetime (read passively from storage, so logging never refreshes
// the session and can't change behaviour).

const readTokenExpiry = () => {
  try {
    const key = Object.keys(window.localStorage).find((k) => /^sb-.+-auth-token$/.test(k));
    if (!key) return { tokenState: 'no_session', tokenExpiresInSec: null };
    const expiresAt = JSON.parse(window.localStorage.getItem(key))?.expires_at;
    if (!expiresAt) return { tokenState: 'unknown', tokenExpiresInSec: null };
    const tokenExpiresInSec = Math.round(expiresAt - Date.now() / 1000);
    return { tokenState: tokenExpiresInSec <= 0 ? 'token_expired' : 'token_valid', tokenExpiresInSec };
  } catch (e) {
    return { tokenState: 'unknown', tokenExpiresInSec: null };
  }
};

/** Collect what is known about a failed save. Pure apart from reading storage/navigator. */
export function describeSaveError(error, { where, transcriptChars, notesChars } = {}) {
  const { tokenState, tokenExpiresInSec } = readTokenExpiry();
  return {
    where,
    name: error?.name || null,
    message: String(error?.message || error || '').slice(0, 300),
    code: error?.code || null,
    status: error?.status ?? null,
    details: typeof error?.details === 'string' ? error.details.slice(0, 300) : null,
    hint: error?.hint || null,
    online: typeof navigator !== 'undefined' ? navigator.onLine : null,
    visibility: typeof document !== 'undefined' ? document.visibilityState : null,
    tokenState,
    tokenExpiresInSec,
    transcriptChars: transcriptChars ?? null,
    notesChars: notesChars ?? null,
  };
}

/** Log a failed transcript save to the console and analytics. Never throws. */
export function logTranscriptSaveFailure(error, context = {}) {
  try {
    const info = describeSaveError(error, context);
    console.error('[transcript-save] failed', info);
    // Analytics only accepts allowlisted keys (enforced by tikcare-analytics).
    track(EVENTS.TRANSCRIPT_SAVE_FAILED, {
      type: info.where,
      error_code: [info.status, info.code || info.name].filter((v) => v != null && v !== '').join(':') || 'unknown',
      reason: info.message,
      stage: info.tokenState,
      count: info.transcriptChars,
    });
    return info;
  } catch (e) {
    return null;
  }
}
